
/**
 * Utility functions for handling Jenjang (Educational Level) normalization and comparison.
 * Solves the issue of inconsistent naming (e.g., "SMP" vs "SMPIT-TQ").
 */

export const JENJANG_SHORT_TO_LONG: Record<string, string> = {
    'PAUD': 'PAUDIT',
    'SD': 'SDIT-TQ',
    'SMP': 'SMPIT-TQ',
    'SMA': 'SMAIT-TQ'
};

export const JENJANG_MAPPING: Record<string, string> = {
    'PAUD': 'PAUD',
    'PAUDIT': 'PAUD',
    'SD': 'SD',
    'SDIT': 'SD',
    'SDIT-TQ': 'SD',
    'SMP': 'SMP',
    'SMPIT': 'SMP',
    'SMPIT-TQ': 'SMP',
    'SMA': 'SMA',
    'SMAIT': 'SMA',
    'SMAIT-TQ': 'SMA',
};

/**
 * Normalizes a Jenjang string to its canonical short form.
 * e.g., "SMPIT-TQ" -> "SMP", "Class SMP A" -> "SMP" (if strict=false)
 *
 * @param jenjang The input string
 * @returns The normalized short form (PAUD, SD, SMP, SMA) or the original uppercased string if not found.
 */
export const normalizeJenjang = (jenjang: any): string => {
    if (!jenjang) return '';

    const upper = String(jenjang).toUpperCase().trim();

    // 1. Direct Mapping Lookup
    if (JENJANG_MAPPING[upper]) {
        return JENJANG_MAPPING[upper];
    }

    // 2. Contains Check (Fallback)
    // Checks if the string contains the short form keywords.
    // Order matters (longer matches usually don't overlap in this set, but good practice)
    if (upper.includes('SMA')) return 'SMA';
    if (upper.includes('SMP')) return 'SMP';
    if (upper.includes('SD')) return 'SD';
    if (upper.includes('PAUD')) return 'PAUD';

    return upper;
};

/**
 * Compares two Jenjang strings for equality after normalization.
 * Handles cases like "SMP" == "SMPIT-TQ".
 */
export const areJenjangsEqual = (j1: any, j2: any): boolean => {
    if (!j1 || !j2) return false;
    return normalizeJenjang(String(j1)) === normalizeJenjang(String(j2));
};

/**
 * Returns the long/formal form of a Jenjang (e.g., for display or DB writes if needed).
 * e.g., "SMP" -> "SMPIT-TQ"
 */
export const getJenjangLongForm = (jenjang: any): string => {
    const short = normalizeJenjang(String(jenjang));
    return JENJANG_SHORT_TO_LONG[short] || String(jenjang); // Return mapped long form or original if unknown
};

/**
 * Checks if a target jenjang matches a filter jenjang.
 * Useful for array filtering. Can handle array of filters.
 */
export const matchesJenjang = (target: any, filter: any): boolean => {
    if (!filter) return true; // No filter matches all

    if (Array.isArray(filter)) {
        if (filter.length === 0) return true;
        return filter.some(f => areJenjangsEqual(String(target), String(f)));
    }

    return areJenjangsEqual(String(target), String(filter));
};

/**
 * Attempts to derive Jenjang from a Class Name.
 * e.g. "7A" -> "SMP", "X IPA" -> "SMA", "10A" -> "SMA"
 */
export const getJenjangFromClass = (className: any): string => {
    if (!className) return '';
    const upper = String(className).toUpperCase();

    // 1. Explicit Check
    const normalized = normalizeJenjang(upper);
    if (['PAUD', 'SD', 'SMP', 'SMA'].includes(normalized)) return normalized;

    // 2. Keyword Check
    if (upper.includes('SMA') || upper.includes('MA') || upper.includes('ALIYAH')) return 'SMA';
    if (upper.includes('SMP') || upper.includes('MTS')) return 'SMP';
    if (upper.includes('SD') || upper.includes('MI')) return 'SD';
    if (upper.includes('PAUD') || upper.includes('TK')) return 'PAUD';

    // 3. Grade Number Heuristic
    // Extract leading number or roman numeral
    // Roman: X, XI, XII (SMA), VII, VIII, IX (SMP), I, II, III, IV, V, VI (SD)

    // SMA Roman (e.g. X, XI, XII, X-1, XII IPA 1)
    if (upper.match(/^(X|XI|XII)([-\s]|$)/)) return 'SMA';
    // SMP Roman (e.g. VII, VIII, IX, VII-A)
    if (upper.match(/^(VII|VIII|IX)([-\s]|$)/)) return 'SMP';
    // SD Roman (I to VI, e.g. VI-A)
    if (upper.match(/^(I|II|III|IV|V|VI)([-\s]|$)/)) return 'SD';

    // Numeric
    const match = upper.match(/^(\d+)/);
    if (match) {
        const num = parseInt(match[1]);
        if (num >= 10 && num <= 12) return 'SMA';
        if (num >= 7 && num <= 9) return 'SMP';
        if (num >= 1 && num <= 6) return 'SD';
    }

    // Default fallback (maybe return 'SMP' if unsure?)
    return '';
};

/**
 * Determines if a class is a "Putra" (Male) class based on conventions.
 * Convention:
 * - SMP (7-9): Ends with A, B, C (e.g., 7A, 8B, 9C)
 * - SMA (X-XII): Ends with 1, 2, 3 (e.g., X-1, XI-2, XII-3)
 */
import { KelasRecord } from '../types';

export const isKelasPutra = (className: any, kelasRecords?: KelasRecord[]): boolean => {
    if (!className) return false;
    const upper = String(className).toUpperCase().trim();

    // Database check (Primary Source of Truth)
    if (kelasRecords && kelasRecords.length > 0) {
        // Hapus awalan "KELAS " jika ada untuk pencocokan yang lebih baik
        let searchName = upper;
        if (searchName.startsWith('KELAS ')) {
            searchName = searchName.replace('KELAS ', '').trim();
        }

        const foundClass = kelasRecords.find(k => String(k.namaKelas).toUpperCase().trim() === searchName);
        if (foundClass) {
            return foundClass.lokasi === 'Putra';
        }
    }

    // Fallback: Check SMA Convention: Roman Numeral (X, XI, XII) followed by 1, 2, or 3
    // Matches "X-1", "X 1", "XII-3", etc.
    if (upper.match(/\b(X|XI|XII)([-\s]?)(1|2|3)\b/)) return true;

    // Fallback: Check SMP Convention: Number (7, 8, 9) or Roman (VII..) followed by A, B, or C
    // Matches "7A", "7-A", "8 B", "9C", etc.
    if (upper.match(/\b(7|8|9|VII|VIII|IX)([-\s]?)(A|B|C)\b/)) return true;

    // Default to false (Putri or Mixed)
    return false;
};

/**
 * Determines if a class is a "Putri" (Female) class.
 * Convention: "Sisa kelas dari kelas putra" (Everything not Putra).
 */
export const isKelasPutri = (className: any, kelasRecords?: KelasRecord[]): boolean => {
    if (!className) return false;
    const upper = String(className).toUpperCase().trim();

    // Database check (Primary Source of Truth)
    if (kelasRecords && kelasRecords.length > 0) {
        let searchName = upper;
        if (searchName.startsWith('KELAS ')) {
            searchName = searchName.replace('KELAS ', '').trim();
        }

        const foundClass = kelasRecords.find(k => String(k.namaKelas).toUpperCase().trim() === searchName);
        if (foundClass) {
            return foundClass.lokasi === 'Putri';
        }
    }

    return !isKelasPutra(String(className), kelasRecords);
};

/**
 * Extracts the base grade/level from a class name.
 * e.g., "7A" -> "7", "X-1" -> "X", "VII-C" -> "VII"
 */
export const extractTingkatDariKelas = (className: any): string => {
    if (!className) return '';
    let upper = String(className).toUpperCase().trim();

    // Hapus awalan "KELAS " jika ada (misal: "KELAS X-1" -> "X-1")
    if (upper.startsWith('KELAS ')) {
        upper = upper.replace('KELAS ', '').trim();
    }

    // SMA Roman (e.g. X, XI, XII)
    // Ordered by length to avoid matching 'X' when it's 'XII'
    const smaMatch = upper.match(/^(XII|XI|X)(?=[-\sA-Z0-9]|$)/);
    if (smaMatch) return smaMatch[1];

    // SMP Roman (e.g. VII, VIII, IX)
    const smpMatch = upper.match(/^(VIII|VII|IX)(?=[-\sA-Z0-9]|$)/);
    if (smpMatch) return smpMatch[1];

    // SD Roman (e.g. I, II, III, IV, V, VI)
    const sdMatch = upper.match(/^(III|IV|VI|II|V|I)(?=[-\s]|$)/);
    if (sdMatch) return sdMatch[1];

    // Numeric (e.g. 7A, 10-1)
    const numMatch = upper.match(/^(\d+)/);
    if (numMatch) return numMatch[1];

    return upper; // Fallback to full name if no pattern matches
};
